import React from 'react'
import styled from 'styled-components'
import Plot from 'react-plotly.js'

import Card from 'components/Card'
import StatusButton, { StatusButtonProps } from 'pages/Dashboard/cards/shared/StatusButton'

import useScreenSize from 'hooks/useScreenSize'

import { color } from 'styles/colors'
import { screenSize } from 'styles/shared'

type Props = {
  title: string
  description: string
  image?: string | null
  plotData?: Array<Plotly.Data> | null
  buttons: Array<StatusButtonProps>
  isLoaded?: boolean
  videoKey?: string | null
  hehmEnabled?: boolean
}

const CardWithButtons = ({ title, description, image = null, plotData = null, buttons, isLoaded = true, videoKey = null, hehmEnabled = false }: Props) => {
  const screenSize = useScreenSize()

  const imageWidth = screenSize === 'small' ? 70 : 90

  return (
    <S.Card title={title} isLoaded={isLoaded} videoKey={videoKey} hehmEnabled={hehmEnabled}>
      <S.Description>{description}</S.Description>

      <S.ImageAndButtons>
        {image && <S.Image src={image} imageWidth={imageWidth} />}
        {plotData &&
          <S.Plot
            data={plotData}
            layout={{
              height: imageWidth,
              width: imageWidth,
              showlegend: false,
              margin: { t: 0, b: 0, l: 0, r: 0 }
            }}
            config={{
              displayModeBar: false,
              showLink: false
            }}
          />
        }
        <S.Buttons>
          {buttons.map(button => <StatusButton {...button} key={button.text} />)}
        </S.Buttons>
      </S.ImageAndButtons>
    </S.Card>
  )
}

export default CardWithButtons

const S = {
  Card: styled(Card).withConfig({
    shouldForwardProp: (prop) => !['hehmEnabled'].includes(prop)
  })<{hehmEnabled: boolean }>`
  ${({ hehmEnabled }) =>
    hehmEnabled &&
    `
      border-radius: 16px;
      border: 1px solid ${color('dashboardCardBorder')};
      font-family: 'Inter', regular;
    `}
  `,
  Description: styled.div`
    font-size: 10pt;
    font-family: inter.regular;
    color: ${color('cardDescription')};
    min-height: 60px;

    @media ${screenSize.small} {
      min-height: unset;
    }
  `,
  ImageAndButtons: styled.div`
    display: flex;
    margin-top: 17px;
  `,
  Image: styled.img.withConfig({
    shouldForwardProp: (prop) => !['imageWidth'].includes(prop)
  })<{ imageWidth: number }>`
    display: inline-block;
    height: ${({ imageWidth }) => imageWidth}px;
    width: ${({ imageWidth }) => imageWidth}px;
  `,
  Plot: styled(Plot)`
    display: inline-block;
  `,
  Buttons: styled.div`
    display: inline-flex;
    flex: 1;
    flex-direction: column;
    margin-left: 20px;

    @media ${screenSize.small} {
      margin-left: 15px;
    }

    > *:not(:first-child) {
      margin-top: 10px;
    }
  `
}
